/**
 * vue-i18n integration helpers shared across UI and SI.
 *
 * Both apps run their own vue-i18n instance, configured per-app in
 * boot/i18n.ts (different available languages, plural rules, message
 * bundles). This module owns the *engine glue* — the module-level
 * instance reference, the safe `t()`/`tm()`/`te()` wrappers, and the
 * pre-boot fallback — so each app's Pinia i18n store stays small and
 * the runtime behavior is identical.
 *
 * Per-app responsibilities (NOT in this module):
 *   - creating the vue-i18n instance (boot/i18n.ts)
 *   - lazy-loading message bundles (loadLanguage)
 *   - persistence config and listing of available languages
 *   - any UI-specific format helpers (date/number/currency/etc)
 *
 * The contract for shared components (`useSharedI18n` from
 * `./registry`) is separate and only depends on `t(key, params?)`.
 */

import type { I18n, Composer } from 'vue-i18n';

/**
 * Module-level reference to the vue-i18n instance. Each app sets its
 * own instance once during boot via `setI18nInstance`. Stored at
 * module scope so non-component callers (other Pinia stores, services)
 * can read it without provide/inject.
 */
let i18nInstance: I18n | null = null;

/**
 * Register the per-app vue-i18n instance. Call once during boot,
 * before any code calls into the engine.
 */
export function setI18nInstance(instance: I18n): void {
  i18nInstance = instance;
}

/**
 * Resolve the vue-i18n composer (the active translation namespace).
 * Returns `null` if no instance has been registered yet — callers
 * must handle pre-boot bootstrap.
 */
export function getI18nComposer(): Composer | null {
  if (!i18nInstance) return null;
  return i18nInstance.global as Composer;
}

/** Parameters bag accepted by the wrapped `t` function. */
export type TranslationParams = Record<string, string | number | boolean>;

/**
 * Pre-boot bootstrap: returns the key with `{name}` placeholder
 * substitution. Only fires if `t()` is invoked before
 * `setI18nInstance` has run.
 */
function fallbackT(key: string, params?: TranslationParams): string {
  if (!params) return key;
  let result = key;
  for (const [k, v] of Object.entries(params)) {
    result = result.replace(`{${k}}`, String(v));
  }
  return result;
}

/**
 * Translate a key with optional pluralization and parameter substitution.
 *
 * Overload preserved for compatibility with both apps' historical signatures:
 *   translateKey(key)                              // basic
 *   translateKey(key, params)                      // named params
 *   translateKey(key, count)                       // pluralization
 *   translateKey(key, count, params)               // plural + named params
 *
 * vue-i18n internally uses `(key, named, choice)` ordering; this
 * wrapper translates from the legacy `(key, count, params)` form.
 */
export function translateKey(
  key: string,
  countOrParams?: number | TranslationParams,
  params?: TranslationParams,
): string {
  const composer = getI18nComposer();
  if (!composer) {
    return fallbackT(key, typeof countOrParams === 'number' ? params : countOrParams);
  }
  try {
    // vue-i18n's `t` is a union of two heavily overloaded function
    // types; TS can't pick the right overload across our callers, so
    // we go through `unknown` and a callable type alias.
    const callT = composer.t as unknown as (
      key: string,
      arg2?: TranslationParams | number,
      arg3?: number,
    ) => string;
    if (typeof countOrParams === 'number') {
      return params ? callT(key, params, countOrParams) : callT(key, countOrParams);
    }
    if (countOrParams) {
      return callT(key, countOrParams);
    }
    return callT(key);
  } catch {
    return key;
  }
}

/**
 * Return the raw (nested) translation value for a key — mirrors
 * vue-i18n's `tm()`. Returns `undefined` if the key is missing or
 * vue-i18n returns an empty stub object.
 */
export function getRawValue(key: string): unknown {
  const composer = getI18nComposer();
  if (!composer) return undefined;
  try {
    const result = (composer.tm as (k: string) => unknown)(key);
    if (
      result &&
      typeof result === 'object' &&
      !Array.isArray(result) &&
      Object.keys(result as object).length === 0
    ) {
      return undefined;
    }
    return result;
  } catch {
    return undefined;
  }
}

/**
 * Existence check — mirrors vue-i18n's `te()`.
 */
export function keyExists(key: string, language?: string): boolean {
  const composer = getI18nComposer();
  if (!composer) return false;
  try {
    const callTe = composer.te as unknown as (k: string, l?: string) => boolean;
    return callTe(key, language);
  } catch {
    return false;
  }
}

/**
 * Switch the composer's active locale, lazy-loading the message
 * bundle if vue-i18n hasn't seen it yet. Returns `true` on success.
 */
export async function applyLanguage(
  language: string,
  loadLanguage: (lang: string) => Promise<unknown>,
): Promise<boolean> {
  const composer = getI18nComposer();
  if (!composer) return false;

  try {
    if (!composer.availableLocales.includes(language)) {
      const messages = await loadLanguage(language);
      // Each app declares its own message schema via `declare module
// 'vue-i18n'`; cast through unknown so shared code stays generic.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(composer.setLocaleMessage as any)(language, messages);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (composer.locale as any).value = language;
    return true;
  } catch {
    return false;
  }
}

/**
 * Lazy-load a language's message bundle into vue-i18n without
 * switching to it. No-op if already loaded or instance not ready.
 */
export async function preloadLanguageMessages(
  language: string,
  loadLanguage: (lang: string) => Promise<unknown>,
): Promise<void> {
  const composer = getI18nComposer();
  if (!composer) return;
  if (composer.availableLocales.includes(language)) return;
  try {
    const messages = await loadLanguage(language);
    // Each app declares its own message schema via `declare module
// 'vue-i18n'`; cast through unknown so shared code stays generic.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(composer.setLocaleMessage as any)(language, messages);
  } catch {
    // Caller decides whether to surface the failure.
  }
}

/**
 * Resolve a language's native display name. Tries the supplied
 * `nameMap` first, then `Intl.DisplayNames`, then falls back to the
 * language code.
 */
export function resolveLanguageDisplayName(
  languageCode: string,
  nameMap: Record<string, string> = {},
): string {
  const predefined = nameMap[languageCode];
  if (predefined) return predefined;

  try {
    const baseLanguage = languageCode.split('-')[0] || languageCode;
    const intlName = new Intl.DisplayNames([languageCode], { type: 'language' }).of(baseLanguage);
    if (intlName) return intlName;
  } catch {
    // Intl.DisplayNames not supported for this locale; fall through.
  }

  return languageCode;
}
